import React, {
  createRef, useCallback, useEffect, useState, useMemo,
} from 'react';
import {
  Box,
  BoxProps,
  ButtonProps,
  Card,
  CardContent,
  CardHeader, CardProps, Grid, MenuItem, TextField, Typography, Button, useTheme, Checkbox, FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import LogoutIcon from '@mui/icons-material/Logout';

import { TestingDashboardDeleteForm, ITestingDashboardDeleteForm } from '../form/TestingDashboardDeleteForm';
import { ITestingDashboardDeleteFormFields } from '../../schemas';
import { SessionService, TestingDashboardService } from '../../services';
import {
  inputFieldLabel, searchCategory, ratePlanCategory, errorCaseCategory, stateDropDown, stateLabel,
} from '../../constants/testing-dashboard.constants';
import RateSwitchTable from './RateSwitchTable';
import {
  BillEstimateResponse, BillLineItem, CustomerDetails, RangeUsageKWh, Switch,
} from '../../models';
import { BillChargeTable } from './BillChargeTable';

const DashboardWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  marginTop: '20px',
}));

const DeleteEntryCard = styled(Card)<CardProps>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  marginTop: '20px',
}));

const SearchEntryCard = styled(Card)<CardProps>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  marginTop: '20px',
}));

const SearchResultCard = styled(Card)<CardProps>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  marginTop: '20px',
}));

const DeleteButton = styled(LoadingButton)<ButtonProps>(({ theme }) => ({
  marginTop: '16px',
}));

const SearchButton = styled(LoadingButton)<ButtonProps>(({ theme }) => ({
  marginTop: '16px',
}));

const ClearRedisCacheButton = styled(LoadingButton)<ButtonProps>(({ theme }) => ({
  marginTop: '16px',
  alignSelf: 'center',
}));

interface SearchResultProps {
  label: string;
  value: string;
}

let searchResultArr = [] as SearchResultProps[];

function TestingDashboard() {
  const deleteRef = createRef<ITestingDashboardDeleteForm>();
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState('');
  const [errorCodeSearchFn, setErrorCodeSearchFn] = useState('');
  const [resp, setResp] = useState('');
  const [field, showField] = useState('');
  const [state, setState] = useState('WI');
  const [isVisibleTextField, setIsVisibleTextField] = useState(false);
  const [isVisibleDropdown, setIsVisibleDropdown] = useState(false);
  const [dropdown, setDropdown] = useState(ratePlanCategory);
  const [searchValue, setSearchValue] = useState('');
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResultProps[]>([]);
  const [billLineItems, setBillLineItems] = useState<[string, BillLineItem[]][]>([]);
  const [billEstimateResponse, setBillEstimateResponse] = useState<BillEstimateResponse>();
  const [monthlyUsagesResponse, setMonthlyUsagesResponse] = useState<RangeUsageKWh[]>([]);
  const [customerRatePlan, setCustomerRatePlan] = useState('');
  const [tableData, setTableData] = useState<Switch[]>([]);
  const [clearRedisResp, setClearRedisResp] = useState('');
  const [isRedisRequestLoading, setIsRedisRequestLoading] = useState(false);
  const [validOnly, setValidOnly] = useState(true);

  useEffect(() => {
    const makeApiCall = async () => {
      const data = await TestingDashboardService.getAllSwitchRequests();
      setTableData(data);
    };
    makeApiCall();
  }, []);

  const onSubmit = useCallback(
    async (values: ITestingDashboardDeleteFormFields) => {
      try {
        setIsLoading(true);
        setErrorCode('');
        setResp('');
        const deleteRes = await TestingDashboardService.deleteSwitchRequest(values.spId);
        setResp(deleteRes.message);
        const data = await TestingDashboardService.getAllSwitchRequests();
        setTableData(data);
      } catch (err: any) {
        const errCode = err.response?.data;
        setErrorCode(errCode);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const validOnlyForSpecialErrorCases = useMemo(() => {
    if (field === 'Special Error Cases' && errorCaseCategory.find((x) => x.label === searchValue)?.validOnly) {
      return validOnly;
    }
    return false;
  }, [field, searchValue, validOnly]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    showField(event.target.value);
    setSearchValue('');
    if (inputFieldLabel.includes(event.target.value)) {
      setIsVisibleTextField(true);
      setIsVisibleDropdown(false);
    } else {
      setIsVisibleTextField(false);
      setIsVisibleDropdown(true);
      switch (event.target.value) {
        case 'Rate Plans': setDropdown(ratePlanCategory);
          break;
        case 'Special Error Cases': setDropdown(errorCaseCategory);
          break;
      }
    }
  }, []);

  const logoutHandler = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    SessionService.signOut();
    navigate('/testingdashboard/login');
  }, [navigate]);

  const handleInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.name === 'state-drop-down') {
      setState(event.target.value);
      return;
    }
    setSearchValue(event.target.value);
  }, []);

  const getBillEstimationResponse = useCallback(async (customerDetails: CustomerDetails) => {
    const billEstimateResp = await TestingDashboardService.getBillEstimate(
      customerDetails.servicePointId,
      customerDetails.accountNumber,
    );
    const billEstimation = billEstimateResp.find((x) => x.billEstimation.planCode === customerDetails.ratePlan);

    return {
      billEstimation: billEstimation?.billEstimation!,
      monthlyUsages: billEstimation?.monthlyUsages!,
    };
  }, []);

  const getBillLineItemsResponse = useCallback(
    async (servicePointId: string) => TestingDashboardService.getBillLineItems(servicePointId),
    [],
  );

  const handleClick = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
    try {
      if (field === 'Account Number') { // Search by account number
        setIsSearchLoading(true);
        setErrorCodeSearchFn('');
        searchResultArr = [];
        const searchByAccNoRes = await TestingDashboardService.searchByAccountNo(searchValue);
        const customerDetails = searchByAccNoRes.account;
        const customerDetailFields = Object.keys(customerDetails);
        customerDetailFields.forEach((fields) => {
          const key = fields as unknown as keyof CustomerDetails;
          let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
          fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
          const customerDetailsData = {
            label: fieldLabel,
            value: customerDetails[key].toString(),
          };
          searchResultArr.push(customerDetailsData);
        });

        const { billEstimation, monthlyUsages } = await getBillEstimationResponse(customerDetails);
        const billLineItemsResp = await getBillLineItemsResponse(customerDetails.servicePointId);

        const actualBillHeading = {
          label: intl.formatMessage({ id: 'testing-dashboard-billing-history-header' }),
          value: '',
        };
        searchResultArr.push(actualBillHeading);

        setShowSearchResult(true);
        setSearchResult(searchResultArr);
        setBillLineItems(billLineItemsResp);
        setBillEstimateResponse(billEstimation);
        setMonthlyUsagesResponse(monthlyUsages);
        setCustomerRatePlan(customerDetails.ratePlan);
      } else if (field === 'Rate Plans') { // Search by rate plans
        setIsSearchLoading(true);
        setErrorCodeSearchFn('');
        searchResultArr = [];
        const searchByPlanRes = await TestingDashboardService.searchByPlan(searchValue, validOnly);
        const searchByPlanDetailFields = Object.keys(searchByPlanRes);
        searchByPlanDetailFields.forEach((fields) => {
          const key = fields as unknown as keyof CustomerDetails;
          let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
          fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
          const searchByPlanDetailsData = {
            label: fieldLabel,
            value: searchByPlanRes[key].toString(),
          };
          searchResultArr.push(searchByPlanDetailsData);
        });
        const { billEstimation, monthlyUsages } = await getBillEstimationResponse(searchByPlanRes);
        const billLineItemsResp = await getBillLineItemsResponse(searchByPlanRes.servicePointId);

        const actualBillHeading = {
          label: intl.formatMessage({ id: 'testing-dashboard-billing-history-header' }),
          value: '',
        };
        searchResultArr.push(actualBillHeading);

        setShowSearchResult(true);
        setSearchResult(searchResultArr);
        setBillLineItems(billLineItemsResp);
        setBillEstimateResponse(billEstimation);
        setMonthlyUsagesResponse(monthlyUsages);
        setCustomerRatePlan(searchByPlanRes.ratePlan);
      } else if (field === 'Service Point Id') { // Search by service point id
        setIsSearchLoading(true);
        setErrorCodeSearchFn('');
        searchResultArr = [];
        const searchBySpIdRes = await TestingDashboardService.searchBySpId(searchValue);
        const searchBySpIdDetailFields = Object.keys(searchBySpIdRes);
        searchBySpIdDetailFields.forEach((fields) => {
          const key = fields as unknown as keyof CustomerDetails;
          let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
          fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
          const searchBySpIdDetailsData = {
            label: fieldLabel,
            value: searchBySpIdRes[key].toString(),
          };
          searchResultArr.push(searchBySpIdDetailsData);
        });
        const { billEstimation, monthlyUsages } = await getBillEstimationResponse(searchBySpIdRes);
        const billLineItemsResp = await getBillLineItemsResponse(searchBySpIdRes.servicePointId);

        const actualBillHeading = {
          label: intl.formatMessage({ id: 'testing-dashboard-billing-history-header' }),
          value: '',
        };
        searchResultArr.push(actualBillHeading);

        setShowSearchResult(true);
        setSearchResult(searchResultArr);
        setBillLineItems(billLineItemsResp);
        setBillEstimateResponse(billEstimation);
        setMonthlyUsagesResponse(monthlyUsages);
        setCustomerRatePlan(searchBySpIdRes.ratePlan);
      } else if (field === 'Meter Id') { // Search by meter id
        setIsSearchLoading(true);
        setErrorCodeSearchFn('');
        searchResultArr = [];
        const searchByMeterRes = await TestingDashboardService.searchByMeter(searchValue);
        const searchByMeterDetailFields = Object.keys(searchByMeterRes);
        searchByMeterDetailFields.forEach((fields) => {
          const key = fields as unknown as keyof CustomerDetails;
          let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
          fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
          const searchByMeterDetailsData = {
            label: fieldLabel,
            value: searchByMeterRes[key].toString(),
          };
          searchResultArr.push(searchByMeterDetailsData);
        });
        const { billEstimation, monthlyUsages } = await getBillEstimationResponse(searchByMeterRes);
        const billLineItemsResp = await getBillLineItemsResponse(searchByMeterRes.servicePointId);

        const actualBillHeading = {
          label: intl.formatMessage({ id: 'testing-dashboard-billing-history-header' }),
          value: '',
        };
        searchResultArr.push(actualBillHeading);

        setShowSearchResult(true);
        setSearchResult(searchResultArr);
        setBillLineItems(billLineItemsResp);
        setBillEstimateResponse(billEstimation);
        setMonthlyUsagesResponse(monthlyUsages);
        setCustomerRatePlan(searchByMeterRes.ratePlan);
      } else if (field === 'Special Error Cases') { // Search by error cases
        if (searchValue === 'Service less than 12 months') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const searchByServiceLengthRes = await TestingDashboardService.searchByServiceLength(state);
          const searchByServiceLengthDetailFields = Object.keys(searchByServiceLengthRes);
          searchByServiceLengthDetailFields.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const searchByServiceLengthDetailsData = {
              label: fieldLabel,
              value: searchByServiceLengthRes[key].toString(),
            };
            searchResultArr.push(searchByServiceLengthDetailsData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Payment plan') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const getAccountRes = await TestingDashboardService.getAccountWithPaymentArrangement(validOnlyForSpecialErrorCases, state);
          const getAccountDetailsField = Object.keys(getAccountRes);
          getAccountDetailsField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const getAccountDetailsData = {
              label: fieldLabel,
              value: getAccountRes[key].toString(),
            };
            searchResultArr.push(getAccountDetailsData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Fixed amount bill') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const customer = await TestingDashboardService.getCustomerWithFixedPlan(state);
          const customerField = Object.keys(customer);
          customerField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const searchByFixedPlanData = {
              label: fieldLabel,
              value: customer[key].toString(),
            };
            searchResultArr.push(searchByFixedPlanData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Budget billing') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const getAccountRes = await TestingDashboardService.getAccountWithBudgetPlan(validOnlyForSpecialErrorCases, state);
          const getAccountDetailsField = Object.keys(getAccountRes);
          getAccountDetailsField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const getAccountDetailsData = {
              label: fieldLabel,
              value: getAccountRes[key].toString(),
            };
            searchResultArr.push(getAccountDetailsData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'No active meters') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const customer = await TestingDashboardService.getNoActiveMeterAccount(state);
          const customerField = Object.keys(customer);
          customerField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const searchByFixedPlanData = {
              label: fieldLabel,
              value: customer[key]?.toString() || 'N/A',
            };
            searchResultArr.push(searchByFixedPlanData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Less than 12 months usage data') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const getAccountRes = await TestingDashboardService.getAccountWithLessAnnualUsage(state);
          const getAccountDetailsField = Object.keys(getAccountRes);
          getAccountDetailsField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const getAccountDetailsData = {
              label: fieldLabel,
              value: getAccountRes[key].toString(),
            };
            searchResultArr.push(getAccountDetailsData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Ineligible to switch') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const getAccountRes = await TestingDashboardService.getAccountThatIneligibleToSwitch(state);
          const getAccountDetailsField = Object.keys(getAccountRes);
          getAccountDetailsField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const getAccountDetailsData = {
              label: fieldLabel,
              value: getAccountRes[key].toString(),
            };
            searchResultArr.push(getAccountDetailsData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'High annual usage') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const getAccountRes = await TestingDashboardService.getAccountHavingHighAnnualUsage(validOnlyForSpecialErrorCases, state);
          const getAccountDetailsField = Object.keys(getAccountRes);
          getAccountDetailsField.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const getAccountDetailsData = {
              label: fieldLabel,
              value: getAccountRes[key].toString(),
            };
            searchResultArr.push(getAccountDetailsData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Severance') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const customerDetailsOfSeverance = await TestingDashboardService.getAccountWithSeverance(validOnlyForSpecialErrorCases, state);
          const customerDetailsKey = Object.keys(customerDetailsOfSeverance);
          customerDetailsKey.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const searchByFixedPlanData = {
              label: fieldLabel,
              value: customerDetailsOfSeverance[key]?.toString() || 'N/A',
            };
            searchResultArr.push(searchByFixedPlanData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'Recently switched') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const recentlySwitchedCustDetails = await TestingDashboardService.getAccountWithRecentlySwitched(state);
          const customerDetailsKey = Object.keys(recentlySwitchedCustDetails);
          customerDetailsKey.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const searchByFixedPlanData = {
              label: fieldLabel,
              value: recentlySwitchedCustDetails[key]?.toString() || 'N/A',
            };
            searchResultArr.push(searchByFixedPlanData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        } else if (searchValue === 'More than 10% usage difference') {
          setIsSearchLoading(true);
          setErrorCodeSearchFn('');
          searchResultArr = [];
          const moreThanTenPercDiffCustDeatails = await TestingDashboardService.getMoreThanTenPercentageDifference(state);
          const customerDetailsKey = Object.keys(moreThanTenPercDiffCustDeatails);
          customerDetailsKey.forEach((fields) => {
            const key = fields as unknown as keyof CustomerDetails;
            let fieldLabel = fields.replace(/([a-z])([A-Z])/g, '$1 $2');
            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
            const searchByFixedPlanData = {
              label: fieldLabel,
              value: moreThanTenPercDiffCustDeatails[key]?.toString() || 'N/A',
            };
            searchResultArr.push(searchByFixedPlanData);
            setShowSearchResult(true);
            setSearchResult(searchResultArr);
          });
          setBillLineItems([]);
          setBillEstimateResponse(undefined);
          setMonthlyUsagesResponse([]);
          setCustomerRatePlan('');
        }
      }
    } catch (err: any) {
      if (err.response) {
        const errCode = err.response.data.message;
        setErrorCodeSearchFn(errCode);
      } else {
        setErrorCodeSearchFn('Unexpected error occurred');
      }
    } finally {
      setIsSearchLoading(false);
    }
  }, [field, searchValue, getBillEstimationResponse, getBillLineItemsResponse, intl, validOnly, state, validOnlyForSpecialErrorCases]);

  const handleRefresh = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
    try {
      setIsLoading(true);
      const data = await TestingDashboardService.getAllSwitchRequests();
      setTableData(data);
    } catch (error: any) {
      const errCode = error.response.data;
      setErrorCode(errCode);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearRedisCache = useCallback(async (event: React.MouseEvent<HTMLElement>) => {
    try {
      // eslint-disable-next-line no-alert
      if (window.confirm('Do you really want to clear redis cache?')) {
        setIsRedisRequestLoading(true);
        const res = await TestingDashboardService.clearRedisCache();
        setClearRedisResp(res.message);
      }
    } catch (error: any) {
      setClearRedisResp(error.response.data.message);
    } finally {
      setIsRedisRequestLoading(false);
    }
  }, []);

  return (
    <DashboardWrapper>
      <Grid container justifyContent="center">
        <Grid item xs={12} display="flex" justifyContent="right">
          <Button
            variant="contained"
            onClick={logoutHandler}
            endIcon={<LogoutIcon />}
            sx={{
              padding: '0 12px',
              backgroundColor: theme.palette.lightPlan.dark,
              color: theme.palette.mainBackground.contrastText,
            }}
          >
            <FormattedMessage id="congratulations.logout" />
          </Button>
        </Grid>
        <Grid item>
          <Typography variant="h4">
            <FormattedMessage id="rateswitch-testing-dashboard-header" />
          </Typography>
          <DeleteEntryCard>
            <CardHeader
              title={intl.formatMessage({ id: 'testing-dashboard-delete-entry-header' })}
            />
            <Typography variant="body2" color={errorCode ? 'error' : undefined}>
              {errorCode
                ? <ErrorOutlineOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', display: 'inline-flex', margin: '2px' }} />
                : null}
              {errorCode ? <FormattedMessage id={errorCode} /> : null}
            </Typography>
            <Typography variant="body2" color={resp ? 'success.main' : undefined}>
              {resp && !errorCode
                ? <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', display: 'inline-flex', margin: '2px' }} />
                : null}
              {resp ? <FormattedMessage id={resp} /> : null}
            </Typography>
            <Grid container direction="row">
              <Grid item md={7} sm={7} xs={7}>
                <CardContent>
                  <TestingDashboardDeleteForm
                    onSubmit={onSubmit}
                    ref={deleteRef}
                  />
                </CardContent>
              </Grid>
              <Grid item md={5} sm={5} xs={5}>
                <DeleteButton
                  variant="contained"
                  color="error"
                  loading={isLoading}
                  onClick={() => deleteRef.current?.handleSubmit()}
                >
                  <FormattedMessage id="testing-dashboard-delete-entry-button" />
                </DeleteButton>
              </Grid>
            </Grid>
          </DeleteEntryCard>
          <SearchEntryCard>
            <CardHeader
              title={intl.formatMessage({ id: 'testing-dashboard-serach-entry-header' })}
            />
            <CardContent>
              <Grid container spacing={2} direction="column">
                <TextField
                  label={<FormattedMessage id="testing-dashboard-search-category" />}
                  select
                  onChange={handleChange}
                  name="search-category"
                  value={field}
                  inputProps={{ 'data-testid': 'category-dropdown' }}
                  variant="outlined"
                  helperText="Please select a category"
                >
                  {searchCategory.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                {isVisibleTextField ? (
                  <TextField
                    id="outlined-basic"
                    label={field}
                    onChange={handleInput}
                    inputProps={{ 'data-testid': 'input-text' }}
                    variant="outlined"
                    sx={{ marginTop: '10px' }}
                  />
                ) : null}
                {isVisibleDropdown ? (
                  <TextField
                    label={field}
                    value={searchValue}
                    select
                    onChange={handleInput}
                    inputProps={{ 'data-testid': 'input-dropdown' }}
                    variant="outlined"
                    sx={{ marginTop: '10px' }}
                  >
                    {dropdown.map((option) => (
                      <MenuItem key={option.label} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
                {field === 'Special Error Cases'
                && (
                <TextField
                  label={stateLabel}
                  value={state}
                  select
                  onChange={handleInput}
                  inputProps={{ 'data-testid': 'state-dropdown' }}
                  variant="outlined"
                  name="state-drop-down"
                  sx={{ marginTop: '10px' }}
                >
                  {stateDropDown.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                )}
                {field === 'Rate Plans'
                 || (field === 'Special Error Cases' && errorCaseCategory.find((x) => x.label === searchValue)?.validOnly) ? (
                   <FormControlLabel
                     control={
                       <Checkbox checked={validOnly} onChange={() => setValidOnly((prev) => !prev)} />
                    }
                     label={intl.formatMessage({ id: 'testing-dashboard-valid-only-checkbox' })}
                   />
                  ) : null}
                <SearchButton
                  variant="contained"
                  color="primary"
                  loading={isSearchLoading}
                  onClick={handleClick}
                >
                  <FormattedMessage id="testing-dashboard-search-entry-button" />
                </SearchButton>
              </Grid>
            </CardContent>
          </SearchEntryCard>
          <Typography variant="body2" color={errorCodeSearchFn ? 'error' : undefined}>
            {errorCodeSearchFn
              ? <ErrorOutlineOutlinedIcon fontSize="small" sx={{ verticalAlign: 'middle', display: 'inline-flex', margin: '2px' }} />
              : null}
            {errorCodeSearchFn ? <FormattedMessage id={errorCodeSearchFn} /> : null}
          </Typography>
          {showSearchResult && searchValue && !errorCodeSearchFn ? (
            <>
              <SearchResultCard>
                <CardHeader
                  title={intl.formatMessage({ id: 'testing-dashboard-search-results-header' })}
                />
                {searchResult.map((option) => (
                  <Grid container p={1} ml={2} key={option.label}>
                    <Grid item xs={6}>
                      <Typography variant="body1">
                        {option.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">
                        {option.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </SearchResultCard>
              { billEstimateResponse && (
              <BillChargeTable
                customerRatePlan={customerRatePlan}
                billLineItems={billLineItems}
                billEstimation={billEstimateResponse}
                monthlyUsages={monthlyUsagesResponse}
              />
              )}
            </>
          ) : null}
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="center">
          <ClearRedisCacheButton
            variant="contained"
            color="primary"
            loading={isRedisRequestLoading}
            onClick={clearRedisCache}
            data-testid="clear-redis"
          >
            <FormattedMessage id="testing-dashboard.clear-redis-cache" />
          </ClearRedisCacheButton>
        </Grid>
        {clearRedisResp && (
          <Grid item display="flex" mt={2} justifyContent="center">
            <Typography variant="h6">
              {clearRedisResp}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <RateSwitchTable tableData={tableData} refreshHandler={handleRefresh} isLoading={isLoading} />
        </Grid>
      </Grid>
    </DashboardWrapper>
  );
}

export { TestingDashboard };
